import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Container,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert as MuiAlert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { theme } from "../theme/colors";
import { shape } from "../theme/shape";
import { useUser } from "../context/UserContext";
import { fetchPosts, upvotePost, removeUpvote, followPost, unfollowPost, deletePost, reportPost, PostType, acceptCommunityGuidelines } from "../utils/feedApi";
import { blockUser } from "../utils/discoverApi";
import PostCard from "../components/CommunityComponents/PostCard";
import CommunityGuidelinesModal from "../components/CommunityComponents/CommunityGuidelinesModal";
import VerifyIdentityModal from "../components/VerifyIdentityModal";
import AuthRequiredModal from "../components/AlertComponents/AuthRequiredModal";
import ReportModal from "../components/AlertComponents/ReportModal";
import { getFilteredFeedEmptyMessage } from "../utils/feedEmptyStateCopy";
import { useNavigate } from "react-router-dom";
import TOPICS from "../utils/topicLabels";
import { isPoliticianAccount } from "../utils/accountTypes";
import { POST_TYPE_FILTER_LABELS } from "../utils/postTypeConfig";

const isVerificationRequiredError = (err) =>
  err?.response?.data?.code === "verification_required";

const getFeedErrorMessage = () =>
  "We couldn't load posts right now. Check your connection and try again.";

/** Shared MUI Outlined Select styles for toolbar (matching chevron + height). */
const toolbarSelectIconSx = {
  color: "#64748b",
  fontSize: 20,
  right: 8,
  top: "50%",
  transform: "translateY(-50%)",
};

const toolbarSelectSx = {
  borderRadius: shape.radius.control,
  fontWeight: 600,
  fontSize: "14px",
  color: "#0f172a",
  height: 38,
  boxSizing: "border-box",
  bgcolor: "#f8fafc",
  alignItems: "center",
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    minHeight: "0 !important",
    py: 0,
    pl: "14px !important",
    pr: "32px !important",
    lineHeight: 1.25,
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": toolbarSelectIconSx,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary, borderWidth: 1.5 },
};

export default function CommunityPage() {
  const navigate = useNavigate();
  const { user, userId, verifiedUser, hasAcceptedCommunityGuidelines, setHasAcceptedCommunityGuidelines, loadUserContext, accountTypeName, loadingUser } = useUser() || {};
  const isLoggedIn = !!user;
  const authResolved = loadingUser === false;
  const isVerified = typeof verifiedUser === "boolean" ? verifiedUser : false;
  const isPolitician = isPoliticianAccount(accountTypeName);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Filters
  const [postType, setPostType] = useState("");
  const [followingOnly, setFollowingOnly] = useState(false);
  const [sortBy, setSortBy] = useState("recent");
  const [geoScope, setGeoScope] = useState("everyone");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [verifiedCountsOnly, setVerifiedCountsOnly] = useState(false);
  const [topicFilter, setTopicFilter] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Modals
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyModalCommunityEngagement, setVerifyModalCommunityEngagement] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [postToReport, setPostToReport] = useState(null);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [feedError, setFeedError] = useState(null);
  const showToast = (message, severity = "success") => setToast({ open: true, message, severity });

  const openVerifyModalForEngagement = useCallback(() => {
    setVerifyModalCommunityEngagement(true);
    setShowVerifyModal(true);
  }, []);

  const closeVerifyModal = useCallback(() => {
    setShowVerifyModal(false);
    setVerifyModalCommunityEngagement(false);
  }, []);

  const handleActionCountChange = useCallback((post, { logged, actionCount, verifiedActionCount, delta }) => {
    setPosts((prevPosts) =>
      prevPosts.map((p) =>
        p.id === post.id
          ? {
              ...p,
              action_count:
                typeof actionCount === "number"
                  ? actionCount
                  : Math.max(0, (p.action_count || 0) + delta),
              verified_action_count:
                typeof verifiedActionCount === "number"
                  ? verifiedActionCount
                  : isVerified
                    ? Math.max(0, (p.verified_action_count ?? 0) + delta)
                    : p.verified_action_count,
              viewer_has_logged_action: logged,
            }
          : p
      )
    );
  }, [isVerified]);

  const loadPosts = useCallback(async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      const filters = {
        page: pageNum,
        page_size: 20,
      };
      if (postType) filters.post_type = postType;
      if (topicFilter) filters.topic = topicFilter;
      if (followingOnly) filters.following_only = true;
      if (sortBy === "saved") {
        filters.followed_posts_only = true;
        filters.sort_by = "recent";
      } else if (sortBy) {
        filters.sort_by = sortBy;
      }
      if (geoScope !== "everyone") filters.geo_scope = geoScope;
      if (verifiedOnly) filters.verified_only = true;

      const data = await fetchPosts(filters);
      const raw = Array.isArray(data) ? data : data.results || [];
      const results = raw.map((p) => ({
        ...p,
        has_upvoted: p.has_upvoted ?? p.viewer_has_upvoted,
        has_followed: p.has_followed ?? p.viewer_is_following,
        follower_count: p.follower_count ?? p.follow_count ?? 0,
      }));
      if (append) {
        setPosts(prev => [...prev, ...results]);
      } else {
        setPosts(results);
      }
      
      setHasMore(results.length === 20); // Assuming 20 is page size
      setFeedError(null);
    } catch (err) {
      console.error("Failed to load posts", err);
      if (!append) {
        setFeedError(getFeedErrorMessage());
      }
    } finally {
      setLoading(false);
    }
  }, [postType, topicFilter, followingOnly, sortBy, geoScope, verifiedOnly]);

  useEffect(() => {
    if (!authResolved) return;
    setPage(1);
    loadPosts(1, false);
  }, [loadPosts, authResolved]);

  useEffect(() => {
    if (!authResolved || isLoggedIn) return;
    setSortBy((prev) => (prev === "saved" ? "recent" : prev));
    setFollowingOnly(false);
    setGeoScope("everyone");
  }, [authResolved, isLoggedIn]);

  useEffect(() => {
    if (!authResolved || isLoggedIn) return;
    if (!sessionStorage.getItem("community_guest_prompt_shown")) {
      sessionStorage.setItem("community_guest_prompt_shown", "1");
      setShowAuthModal(true);
    }
  }, [authResolved, isLoggedIn]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadPosts(nextPage, true);
    }
  };

  const clearFilters = useCallback(() => {
    setPostType("");
    setTopicFilter("");
    setFollowingOnly(false);
    setGeoScope("everyone");
    setVerifiedOnly(false);
    setVerifiedCountsOnly(false);
  }, []);

  const hasOtherFeedFilters =
    Boolean(postType) ||
    Boolean(topicFilter) ||
    followingOnly ||
    geoScope !== "everyone";

  const activeFilterCount =
    (postType ? 1 : 0) +
    (topicFilter ? 1 : 0) +
    (followingOnly ? 1 : 0) +
    (geoScope !== "everyone" ? 1 : 0) +
    (verifiedOnly ? 1 : 0) +
    (verifiedCountsOnly ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  const handleCreatePostClick = () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    if (!hasAcceptedCommunityGuidelines) {
      setShowGuidelinesModal(true);
      return;
    }
    navigate("/community/create");
  };

  const handleAcceptGuidelines = async () => {
    setGuidelinesLoading(true);
    try {
      await acceptCommunityGuidelines();
      if (setHasAcceptedCommunityGuidelines) {
        setHasAcceptedCommunityGuidelines(true);
      }
      if (loadUserContext) {
        await loadUserContext();
      }
      setShowGuidelinesModal(false);
      navigate("/community/create");
    } catch (err) {
      console.error("Error accepting guidelines:", err);
      showToast("We couldn't save your guidelines acceptance. Check your connection and try again.", "error");
    } finally {
      setGuidelinesLoading(false);
    }
  };

  const handleUpvote = async (post) => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }

    try {
      // Optimistic update
      const currentlyUpvoted = post.has_upvoted ?? post.viewer_has_upvoted;
      const upvoteDelta = currentlyUpvoted ? -1 : 1;
      setPosts(prev => prev.map(p => {
        if (p.id === post.id) {
          const next = {
            ...p,
            has_upvoted: !currentlyUpvoted,
            upvote_count: currentlyUpvoted ? Math.max(0, (p.upvote_count || 0) - 1) : (p.upvote_count || 0) + 1
          };
          if (isVerified) {
            next.verified_upvote_count = Math.max(0, (p.verified_upvote_count ?? 0) + upvoteDelta);
          }
          return next;
        }
        return p;
      }));

      if (currentlyUpvoted) {
        await removeUpvote(post.id);
      } else {
        await upvotePost(post.id);
      }
    } catch (err) {
      console.error("Failed to toggle upvote", err);
      if (isVerificationRequiredError(err)) {
        openVerifyModalForEngagement();
      } else {
        showToast("We couldn't update your upvote. Please try again.", "error");
      }
      loadPosts(1, false);
    }
  };

  const handleFollow = async (post) => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }

    const currentlyFollowed = post.has_followed ?? post.viewer_is_following;
    const followDelta = currentlyFollowed ? -1 : 1;

    try {
      setPosts(prev => prev.map(p => {
        if (p.id === post.id) {
          const next = {
            ...p,
            has_followed: !currentlyFollowed,
            follower_count: currentlyFollowed ? Math.max(0, (p.follower_count || p.follow_count || 0) - 1) : (p.follower_count || p.follow_count || 0) + 1
          };
          if (isVerified) {
            next.verified_follow_count = Math.max(0, (p.verified_follow_count ?? 0) + followDelta);
          }
          return next;
        }
        return p;
      }));

      if (currentlyFollowed) {
        await unfollowPost(post.id);
      } else {
        await followPost(post.id);
      }
    } catch (err) {
      console.error("Failed to toggle follow", err);
      if (isVerificationRequiredError(err)) {
        openVerifyModalForEngagement();
      } else {
        showToast("We couldn't update your saved post. Please try again.", "error");
      }
      loadPosts(1, false);
    }
  };

  const handleDelete = async (post) => {
    try {
      await deletePost(post.id);
      setPosts(prev => prev.filter(p => p.id !== post.id));
    } catch (err) {
      console.error("Failed to delete post", err);
    }
  };

  const handleEdit = (post) => {
    navigate("/community/create", { state: { editPostId: post.id } });
  };

  const handleReport = (post) => {
    setPostToReport(post);
    setShowReportModal(true);
  };

  const handleBlock = async ({ userId: authorId, name }) => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    if (!authorId) return;
    try {
      await blockUser(authorId);
      setPosts((prev) => prev.filter((p) => (p.user?.id || p.user_id) !== authorId));
      showToast(`${name} has been blocked.`);
    } catch (err) {
      console.error("Failed to block user", err);
      const message = err?.message || "Couldn't block this account. Please try again.";
      showToast(message, "error");
      throw err;
    }
  };

  const handleReportSubmit = async (reason, details) => {
    if (!postToReport) return;
    if (!isLoggedIn) {
      setShowReportModal(false);
      setPostToReport(null);
      setShowAuthModal(true);
      return;
    }
    try {
      await reportPost(postToReport.id, reason, details);
      setShowReportModal(false);
      setPostToReport(null);
      showToast("Post reported successfully.");
    } catch (err) {
      console.error("Failed to report post", err);
      const msg = err?.response?.data?.error || "";
      if (isVerificationRequiredError(err)) {
        setShowReportModal(false);
        setPostToReport(null);
        openVerifyModalForEngagement();
      } else {
        showToast(msg || "We couldn't submit your report. Please try again.", "error");
      }
      throw err;
    }
  };


  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", p: 2 }}>
      <Container maxWidth="md" sx={{ px: 0 }}>
        {/* Toolbar */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "14px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
              px: { xs: 1.5, sm: 2.5 },
              py: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", mr: 0.25 }}>
                  Sort
                </Typography>
                <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 160 } }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={{
                      ...toolbarSelectSx,
                      bgcolor: "#fff",
                      border: "1px solid #cbd5e1",
                      "&:hover": { borderColor: "#94a3b8" },
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    }}
                  >
                    <MenuItem value="recent">Recent</MenuItem>
                    <MenuItem value="most_upvoted">Most Upvoted</MenuItem>
                    {postType === PostType.IDEA && <MenuItem value="most_active">Most Active</MenuItem>}
                    {isLoggedIn && <MenuItem value="saved">Saved</MenuItem>}
                  </Select>
                </FormControl>

                {hasActiveFilters && (
                  <Button
                    onClick={clearFilters}
                    sx={{
                      textTransform: "none",
                      color: "#0f172a",
                      textDecoration: "underline",
                      fontWeight: 600,
                      minWidth: "auto",
                      px: 0.75,
                    }}
                  >
                    Clear
                  </Button>
                )}

                <Button
                  onClick={() => setShowFilterModal(true)}
                  sx={{
                    minWidth: "auto",
                    height: 40,
                    px: 1.5,
                    borderRadius: shape.radius.control,
                    background: hasActiveFilters ? theme.gradient : "#fff",
                    color: hasActiveFilters ? "#fff" : theme.primary,
                    border: hasActiveFilters ? "none" : `1px solid ${theme.primary}`,
                    position: "relative",
                    textTransform: "none",
                    fontSize: 13,
                    fontWeight: 700,
                    boxShadow: hasActiveFilters
                      ? "0 1px 3px rgba(4, 120, 87, 0.25)"
                      : "none",
                    "&:hover": hasActiveFilters
                      ? { background: theme.gradient, opacity: 0.92 }
                      : { bgcolor: "#f0fdf4", borderColor: theme.primaryHover, color: theme.primaryHover },
                  }}
                  aria-label="Open filters"
                >
                  Filters
                  {hasActiveFilters && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -4,
                        right: -6,
                        minWidth: 18,
                        height: 18,
                        borderRadius: shape.radius.pill,
                        bgcolor: "#fff",
                        border: `2px solid ${theme.primary}`,
                        fontSize: 10,
                        fontWeight: 700,
                        color: theme.primary,
                        lineHeight: "14px",
                      }}
                    >
                      {activeFilterCount}
                    </Box>
                  )}
                </Button>
              </Box>

              {authResolved && isLoggedIn && (
                <Button
                  onClick={handleCreatePostClick}
                  sx={{
                    color: "#fff",
                    fontWeight: 650,
                    fontSize: "14px",
                    textTransform: "none",
                    background: theme.gradient,
                    borderRadius: "10px",
                    px: 2.5,
                    height: 40,
                    flexShrink: 0,
                    boxShadow: "0 1px 3px rgba(4, 120, 87, 0.25)",
                    "&:hover": {
                      background: theme.gradient,
                      opacity: 0.92,
                      boxShadow: "0 2px 6px rgba(4, 120, 87, 0.35)",
                    },
                  }}
                >
                  Create Post
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        <Dialog
          open={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: shape.radius.modal, maxHeight: "85vh" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 1.25,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Filters
            <IconButton onClick={() => setShowFilterModal(false)} aria-label="Close filters">
              <CloseRoundedIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
            <Box sx={{ display: "grid", gap: 2 }}>
              {isLoggedIn && (
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1, color: "#0f172a" }}>Location Scope</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {[
                    { value: "everyone", label: "Everywhere" },
                    { value: "national", label: "National" },
                    { value: "state", label: "My state" },
                    { value: "district", label: "My district" },
                  ].map(({ value, label }) => {
                    const active = geoScope === value;
                    return (
                      <Button
                        key={value}
                        onClick={() => setGeoScope(value)}
                        variant={active ? "contained" : "outlined"}
                        sx={{
                          textTransform: "none",
                          borderRadius: shape.radius.pill,
                          minHeight: 34,
                          px: 1.75,
                          ...(active
                            ? { bgcolor: "#0f172a", color: "#fff", "&:hover": { bgcolor: "#0f172a" } }
                            : { color: "#0f172a", borderColor: "#94a3b8" }),
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
                    Verified users
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#475569", lineHeight: 1.35, mt: 0.25 }}>
                    Limit the feed to verified accounts.
                  </Typography>
                </Box>
                <Button
                  onClick={() => setVerifiedOnly((prev) => !prev)}
                  variant={verifiedOnly ? "contained" : "outlined"}
                  aria-label={`Verified users filter, ${verifiedOnly ? "on" : "off"}`}
                  aria-pressed={verifiedOnly}
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    borderRadius: shape.radius.pill,
                    minHeight: 34,
                    minWidth: 56,
                    px: 1.75,
                    ...(verifiedOnly
                      ? { bgcolor: "#0f172a", color: "#fff", "&:hover": { bgcolor: "#0f172a" } }
                      : { color: "#0f172a", borderColor: "#94a3b8" }),
                  }}
                >
                  {verifiedOnly ? "On" : "Off"}
                </Button>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
                    Engagement counts
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#475569", lineHeight: 1.35, mt: 0.25 }}>
                    Count upvotes, comments, saves, polls, and actions from verified users only. Doesn&apos;t change which posts load.
                  </Typography>
                </Box>
                <Button
                  onClick={() => setVerifiedCountsOnly((prev) => !prev)}
                  variant={verifiedCountsOnly ? "contained" : "outlined"}
                  aria-label={`Engagement counts filter, ${verifiedCountsOnly ? "on" : "off"}`}
                  aria-pressed={verifiedCountsOnly}
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    borderRadius: shape.radius.pill,
                    minHeight: 34,
                    minWidth: 56,
                    px: 1.75,
                    ...(verifiedCountsOnly
                      ? { bgcolor: "#0f172a", color: "#fff", "&:hover": { bgcolor: "#0f172a" } }
                      : { color: "#0f172a", borderColor: "#94a3b8" }),
                  }}
                >
                  {verifiedCountsOnly ? "On" : "Off"}
                </Button>
              </Box>

              {isLoggedIn && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
                    People
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#475569", lineHeight: 1.35, mt: 0.25 }}>
                    Show posts from people you follow.
                  </Typography>
                </Box>
                <Button
                  onClick={() => setFollowingOnly((prev) => !prev)}
                  variant={followingOnly ? "contained" : "outlined"}
                  aria-label={`Following filter, ${followingOnly ? "on" : "off"}`}
                  aria-pressed={followingOnly}
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    borderRadius: shape.radius.pill,
                    minHeight: 34,
                    minWidth: 56,
                    px: 1.75,
                    ...(followingOnly
                      ? { bgcolor: "#0f172a", color: "#fff", "&:hover": { bgcolor: "#0f172a" } }
                      : { color: "#0f172a", borderColor: "#94a3b8" }),
                  }}
                >
                  {followingOnly ? "On" : "Off"}
                </Button>
              </Box>
              )}

              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1, color: "#0f172a" }}>Post Type</Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={postType}
                    displayEmpty
                    onChange={(e) => setPostType(e.target.value)}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={toolbarSelectSx}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value={PostType.IDEA}>Ideas</MenuItem>
                    <MenuItem value={PostType.ACTION_LEGISLATION}>Actions</MenuItem>
                    <MenuItem value={PostType.QUESTION}>Questions</MenuItem>
                    <MenuItem value={PostType.POLICY_CREATED}>{POST_TYPE_FILTER_LABELS[PostType.POLICY_CREATED]}</MenuItem>
                    <MenuItem value={PostType.RECENT_WIN}>{POST_TYPE_FILTER_LABELS[PostType.RECENT_WIN]}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1, color: "#0f172a" }}>Topic</Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={topicFilter}
                    displayEmpty
                    onChange={(e) => setTopicFilter(e.target.value)}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={toolbarSelectSx}
                  >
                    <MenuItem value="">All Topics</MenuItem>
                    {TOPICS.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            <Button
              onClick={clearFilters}
              sx={{ textTransform: "none", color: "#475569", fontWeight: 600 }}
            >
              Clear
            </Button>
            <Button
              onClick={() => setShowFilterModal(false)}
              sx={{
                textTransform: "none",
                background: theme.gradient,
                color: "#fff",
                borderRadius: shape.radius.control,
                px: 2,
                minHeight: 36,
                "&:hover": { background: theme.gradient, opacity: 0.92 },
              }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* Feed */}
        <Box>
          {feedError && (
            <MuiAlert severity="error" onClose={() => setFeedError(null)} sx={{ borderRadius: "10px", mb: 2 }}>
              {feedError}
            </MuiAlert>
          )}

          {posts.map(post => (
            <PostCard 
              key={post.id} 
              post={post} 
              currentUserId={userId}
              verifiedCountsOnly={verifiedCountsOnly}
              onUpvote={handleUpvote}
              onFollow={handleFollow}
              onAuthRequired={() => setShowAuthModal(true)}
              isVerified={isVerified}
              onVerifyRequired={openVerifyModalForEngagement}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onReport={handleReport}
              onBlock={handleBlock}
              onActionCountChange={handleActionCountChange}
              onActionError={(message) => showToast(message, "error")}
            />
          ))}
          
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: theme.primary }} />
            </Box>
          )}
          
          {!loading && posts.length === 0 && !feedError && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" sx={{ color: "#64748b", mb: 2 }}>
                {getFilteredFeedEmptyMessage({ verifiedOnly, hasOtherFeedFilters }) ??
                  "No posts yet. Be the first to share something."}
              </Typography>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{
                    color: "var(--primary-color)",
                    borderColor: "var(--primary-color)",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "var(--primary-color-hover)",
                      backgroundColor: "rgba(4, 120, 87, 0.06)",
                      color: "var(--primary-color-hover)",
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {!loading && hasMore && posts.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Button variant="outlined" onClick={handleLoadMore}>Load More</Button>
            </Box>
          )}
        </Box>
      </Container>

      {/* Modals */}
      <CommunityGuidelinesModal 
        open={showGuidelinesModal} 
        onClose={() => setShowGuidelinesModal(false)}
        onAccept={handleAcceptGuidelines}
        loading={guidelinesLoading}
        isPolitician={isPolitician}
      />

      <VerifyIdentityModal 
        isOpen={showVerifyModal} 
        onClose={closeVerifyModal}
        communityEngagement={verifyModalCommunityEngagement}
      />

      <AuthRequiredModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={() => {
          setShowAuthModal(false);
          navigate("/auth/login");
        }}
        title="You're browsing the community"
        description="Sign in to vote, comment, save posts, and join the discussion."
        confirmLabel="Sign in"
        dismissLabel="Keep browsing"
      />

      <ReportModal
        open={showReportModal}
        contentType="post"
        onClose={() => {
          setShowReportModal(false);
          setPostToReport(null);
        }}
        onSubmit={handleReportSubmit}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}